(() => {
	'use strict';

	const SELECTOR_BLOCK = '[data-donation-block]';
	const SELECTOR_BTN = '.donation_button';
	const SELECTOR_LOADER = '.loaderDiv';
	const SELECTOR_MESSAGES = '.messages-wrapper';
	const SELECTOR_MESSAGE = '.messages-wrapper .message';

	const STATE_CLASSES = [
		'show-success',
		'show-ajax-error',
		'show-amount-error',
		'show-over-limit-warning'
	];

	const MAX_DONATION = 1000000;
	const RELOAD_DELAY_MS = 4000;

	function removeAllMessages(blockEl) {
		blockEl.classList.remove(...STATE_CLASSES);
	}

	function setBusy(blockEl, btnEl, isBusy) {
		blockEl.setAttribute('aria-busy', isBusy ? 'true' : 'false');

		if (btnEl) {
			btnEl.disabled = !!isBusy;
			btnEl.setAttribute('aria-disabled', isBusy ? 'true' : 'false');
		}

		const loader = blockEl.querySelector(SELECTOR_LOADER);
		if (loader) loader.style.display = isBusy ? 'flex' : 'none';
	}

	function getInputForButton(blockEl, btnEl) {
		const inputId = btnEl?.dataset?.inputId;
		if (!inputId) return blockEl.querySelector('.textbox_amount');
		return document.getElementById(inputId) || blockEl.querySelector('.textbox_amount');
	}

	function getVariantId(btnEl) {
		const raw = btnEl?.dataset?.variantId;
		const id = raw ? parseInt(raw, 10) : NaN;
		return Number.isFinite(id) ? id : null;
	}

	function parseAmount(value) {
		if (!value) return NaN;

		// supporto input con virgola (IT)
		const normalized = String(value).trim().replace(',', '.');
		const n = parseFloat(normalized);

		if (!Number.isFinite(n)) return NaN;

		// Shopify quantity è int. Arrotondo “a intero più vicino” come avevi fatto tu.
		// Se vuoi sempre per difetto: Math.floor(n)
		return Math.round(n);
	}

	function showMessage(blockEl, stateClass) {
		removeAllMessages(blockEl);
		blockEl.classList.add(stateClass);

		// Accessibilità: focus “soft” sul messaggio visibile
		const messagesWrap = blockEl.querySelector(SELECTOR_MESSAGES);
		if (messagesWrap) {
			if (!messagesWrap.hasAttribute('tabindex')) messagesWrap.setAttribute('tabindex', '-1');
			messagesWrap.focus({ preventScroll: false });
		}
	}

	async function addDonationToCart(variantId, qty) {
		const payload = {
			items: [
				{
					id: variantId,
					quantity: qty
				}
			]
		};

		const res = await fetch('/cart/add.js', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Accept': 'application/json'
			},
			body: JSON.stringify(payload)
		});

		// Shopify può rispondere con JSON anche in errore; gestiamo safe
		let data = null;
		try {
			data = await res.json();
		} catch (e) {}

		if (!res.ok) {
			const err = new Error('Cart add failed');
			err.data = data;
			throw err;
		}

		return data;
	}

	function initDonationBlock(blockEl) {
		const btnEl = blockEl.querySelector(SELECTOR_BTN);
		if (!btnEl) return;

		const inputEl = getInputForButton(blockEl, btnEl);
		if (!inputEl) return;

		const variantId = getVariantId(btnEl);
		if (!variantId) return;

		// A11y: aria-live già ok nel markup. Qui aggiungiamo aria-busy baseline
		blockEl.setAttribute('aria-busy', 'false');

		// Stato interno “over limit”
		let overLimitTriggered = false;
		let clearTimeoutId = null;

		function clearOverLimitTimer() {
			if (!clearTimeoutId) return;
			clearTimeout(clearTimeoutId);
			clearTimeoutId = null;
		}

		function handleOverLimit(amount) {
			// Se rientra nei limiti, ripulisci
			if (overLimitTriggered && amount <= MAX_DONATION) {
				blockEl.classList.remove('show-over-limit-warning');
				overLimitTriggered = false;
				clearOverLimitTimer();
				return;
			}

			// Trigger warning
			if (amount > MAX_DONATION && !overLimitTriggered) {
				overLimitTriggered = true;
				showMessage(blockEl, 'show-over-limit-warning');

				clearTimeoutId = window.setTimeout(() => {
					inputEl.value = '';
					overLimitTriggered = false;
					clearTimeoutId = null;
				}, 10000);
			}
		}

		async function handleSubmit() {
			const amount = parseAmount(inputEl.value);

			// Validazione
			if (!Number.isFinite(amount) || amount < 1) {
				showMessage(blockEl, 'show-amount-error');
				return;
			}

			if (amount > MAX_DONATION) {
				showMessage(blockEl, 'show-over-limit-warning');
				inputEl.value = '';
				return;
			}

			setBusy(blockEl, btnEl, true);

			try {
				await addDonationToCart(variantId, amount);
				showMessage(blockEl, 'show-success');

				// Se hai un drawer/cart ajax, qui potremmo dispatchare un evento invece del reload.
				// Per ora manteniamo il comportamento “sicuro” come prima.
				window.setTimeout(() => {
					window.location.reload();
				}, RELOAD_DELAY_MS);
			} catch (e) {
				showMessage(blockEl, 'show-ajax-error');
				window.setTimeout(() => {
					window.location.reload();
				}, RELOAD_DELAY_MS);
			} finally {
				setBusy(blockEl, btnEl, false);
			}
		}

		// Click
		btnEl.addEventListener('click', (e) => {
			e.preventDefault();
			handleSubmit();
		});

		// Enter sull’input
		inputEl.addEventListener('keydown', (e) => {
			if (e.key !== 'Enter') return;
			e.preventDefault();
			handleSubmit();
		});

		// Input live: gestisci warning over-limit “intelligente”
		inputEl.addEventListener('input', () => {
			const live = parseAmount(inputEl.value);

			// Se non è numero, non spammare warning: pulisci stati “error”
			if (!Number.isFinite(live)) {
				// Non tocchiamo success/error, ma togliamo amount error se sta scrivendo
				blockEl.classList.remove('show-amount-error');
				return;
			}

			handleOverLimit(live);

			// Se l’utente sta correggendo, togli l’amount-error
			if (live >= 1) blockEl.classList.remove('show-amount-error');
		});
	}

	function init(root = document) {
		root.querySelectorAll(SELECTOR_BLOCK).forEach(initDonationBlock);
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => init());
	} else {
		init();
	}
})();
